import React, { useState, useMemo } from 'react'
import {
  Box,
  Card,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Grid,
} from '@mui/material'
import {
  Zap,
  Heart,
  Users,
  TrendingUp,
  Star,
  Target,
  Plus,
  Edit2,
  Trash2,
  Send,
  ExternalLink,
} from 'lucide-react'

// Using your existing FollowerType interface from AnalyzeFollowers
interface TagType {
  name: string
  count: number
}

interface FollowerType {
  username: string
  platform: string
  bio: string
  profileImage: string
  tags: TagType[]
  profileSummary: string
  commentsSummary: string
  stats: {
    followers: string
    posts: string
    following: string
    likes: number
    comments: number
  }
}

interface SegmentCriteria {
  minFollowers?: number
  maxFollowers?: number
  minEngagement?: number
  hashtags?: string[]
  industries?: string[]
  minLikes?: number
  minComments?: number
}

interface Segment {
  id: string
  name: string
  description: string
  criteria: SegmentCriteria
  followers: FollowerType[]
  avgEngagement: number
  color: string
  icon: string
}

const glassmorphicStyle = {
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '24px',
  padding: '24px',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(168, 85, 247, 0.2)',
    background: 'rgba(255, 255, 255, 0.08)',
  },
}

const predefinedSegments: Array<Omit<Segment, 'followers' | 'avgEngagement'>> = [
  {
    id: 'high-engagement',
    name: 'Top Engagers',
    criteria: { minLikes: 500, minComments: 20 },
    color: '#ec4899',
    icon: 'Heart',
    description: 'Your most active supporters - reach out to build relationships',
  },
  {
    id: 'micro-influencers',
    name: 'Collaboration Ready',
    criteria: { minFollowers: 10000, maxFollowers: 100000 },
    color: '#a855f7',
    icon: 'Star',
    description: 'Micro-influencers (10K-100K) - DM for partnerships',
  },
  {
    id: 'growing-accounts',
    name: 'Rising Stars',
    criteria: { minFollowers: 1000, maxFollowers: 10000, minLikes: 100 },
    color: '#10b981',
    icon: 'TrendingUp',
    description: 'Growing creators (1K-10K) - engage early for mutual growth',
  },
]

const iconMap = { Zap, Heart, Users, TrendingUp, Star, Target }

interface NalapodFollowerSegmentationProps {
  followers: FollowerType[]
}

export const NalapodFollowerSegmentation: React.FC<NalapodFollowerSegmentationProps> = ({ followers }) => {
  const [customSegments, setCustomSegments] = useState<Array<Omit<Segment, 'followers' | 'avgEngagement'>>>([])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingSegment, setEditingSegment] = useState<Omit<Segment, 'followers' | 'avgEngagement'> | null>(null)
  const [selectedSegment, setSelectedSegment] = useState<Segment | null>(null)

  // Helper to parse follower count string to number
  const parseFollowerCount = (followerStr: string): number => {
    const num = parseFloat(followerStr.replace(/[^0-9.]/g, ''))
    if (followerStr.includes('M')) return num * 1000000
    if (followerStr.includes('K')) return num * 1000
    return num
  }

  // Calculate engagement rate from likes and comments
  const calculateEngagementRate = (follower: FollowerType): number => {
    const followerCount = parseFollowerCount(follower.stats.followers)
    if (followerCount === 0) return 0
    const totalEngagement = follower.stats.likes + follower.stats.comments
    return (totalEngagement / followerCount) * 100
  }

  // Match follower against segment criteria
  const matchesSegmentCriteria = (follower: FollowerType, criteria: SegmentCriteria): boolean => {
    const followerCount = parseFollowerCount(follower.stats.followers)
    
    if (criteria.minFollowers && followerCount < criteria.minFollowers) return false
    if (criteria.maxFollowers && followerCount > criteria.maxFollowers) return false
    if (criteria.minLikes && follower.stats.likes < criteria.minLikes) return false
    if (criteria.minComments && follower.stats.comments < criteria.minComments) return false
    
    if (criteria.hashtags && criteria.hashtags.length > 0) {
      const bioLower = follower.bio?.toLowerCase() ?? ''
      const hasMatchingHashtag = criteria.hashtags.some(tag =>
        bioLower.includes(tag.toLowerCase())
      )
      if (!hasMatchingHashtag) return false
    }
    
    if (criteria.industries && criteria.industries.length > 0) {
      const bioLower = follower.bio?.toLowerCase() ?? ''
      const tagsLower = follower.tags.map(t => t.name.toLowerCase())
      const hasMatchingIndustry = criteria.industries.some(industry =>
        bioLower.includes(industry.toLowerCase()) ?? 
        tagsLower.some(tag => tag.includes(industry.toLowerCase()))
      )
      if (!hasMatchingIndustry) return false
    }
    
    return true
  }

  // Calculate all segments with follower data
  const allSegments: Segment[] = useMemo(() => {
    return [...predefinedSegments, ...customSegments].map(segment => {
      const matchingFollowers = followers.filter(follower => 
        matchesSegmentCriteria(follower, segment.criteria)
      )
      
      const avgEngagement = matchingFollowers.length > 0
        ? matchingFollowers.reduce((sum, f) => sum + calculateEngagementRate(f), 0) / matchingFollowers.length
        : 0

      return {
        ...segment,
        followers: matchingFollowers,
        avgEngagement,
      }
    })
  }, [followers, customSegments])

  return (
    <Box sx={{ mt: 4 }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        flexWrap: 'wrap',
        gap: 2,
      }}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 1,
              background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Audience Insights
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            {followers.length} followers grouped by engagement and reach - click any segment to reach out
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Plus size={20} />}
          onClick={() => {
            setEditingSegment(null)
            setDialogOpen(true)
          }}
          sx={{
            background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
            borderRadius: '12px',
            padding: '12px 24px',
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: '0 8px 32px rgba(168, 85, 247, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #9333ea 0%, #db2777 100%)',
              boxShadow: '0 12px 40px rgba(168, 85, 247, 0.4)',
            },
          }}
        >
          Create Custom Segment
        </Button>
      </Box>

      {/* Segments Grid */}
      <Grid container spacing={3}>
        {allSegments.map(segment => (
          <Grid item xs={12} sm={6} md={4} key={segment.id}>
            <SegmentCard
              segment={segment}
              onView={() => setSelectedSegment(segment)}
              onEdit={() => {
                setEditingSegment(segment)
                setDialogOpen(true)
              }}
              onDelete={() => setCustomSegments(customSegments.filter(s => s.id !== segment.id))}
              isCustom={!predefinedSegments.some(s => s.id === segment.id)}
            />
          </Grid>
        ))}
      </Grid>

      {/* Custom Segment Dialog */}
      <CustomSegmentDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        editingSegment={editingSegment}
        onSave={(segment) => {
          if (editingSegment) {
            setCustomSegments(customSegments.map(s => s.id === segment.id ? segment : s))
          } else {
            setCustomSegments([...customSegments, segment])
          }
          setDialogOpen(false)
        }}
      />

      {/* Segment Details Dialog */}
      <SegmentDetailsDialog
        segment={selectedSegment}
        open={selectedSegment !== null}
        onClose={() => setSelectedSegment(null)}
      />
    </Box>
  )
}

// Segment Card Component
interface SegmentCardProps {
  segment: Segment
  onView: () => void
  onEdit: () => void
  onDelete: () => void
  isCustom: boolean
}

const SegmentCard: React.FC<SegmentCardProps> = ({ segment, onView, onEdit, onDelete, isCustom }) => {
  const Icon = iconMap[segment.icon as keyof typeof iconMap] ?? Users

  return (
    <Card sx={{ ...glassmorphicStyle, borderLeft: `4px solid ${segment.color}`, position: 'relative' }}>
      {isCustom && (
        <Box sx={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 1 }}>
          <IconButton 
            size="small" 
            onClick={onEdit}
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': { color: '#fff', background: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            <Edit2 size={16} />
          </IconButton>
          <IconButton 
            size="small" 
            onClick={onDelete}
            sx={{ 
              color: 'rgba(239, 68, 68, 0.7)',
              '&:hover': { color: '#ef4444', background: 'rgba(239, 68, 68, 0.1)' }
            }}
          >
            <Trash2 size={16} />
          </IconButton>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '16px',
            background: `linear-gradient(135deg, ${segment.color}30, ${segment.color}60)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 8px 24px ${segment.color}40`,
          }}
        >
          <Icon size={28} style={{ color: segment.color }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, flex: 1, color: '#fff' }}>
          {segment.name}
        </Typography>
      </Box>

      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 3, minHeight: 40 }}>
        {segment.description}
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, color: '#fff' }}>
            {segment.followers.length}
          </Typography>
          <Chip
            label={`${segment.avgEngagement.toFixed(1)}% engagement`}
            size="small"
            sx={{
              background: `${segment.color}30`,
              color: segment.color,
              fontWeight: 600,
              border: `1px solid ${segment.color}60`,
            }}
          />
        </Box>
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
          followers in this segment
        </Typography>
      </Box>

      <Button
        fullWidth
        variant="outlined"
        onClick={onView}
        startIcon={<Users size={16} />}
        sx={{
          borderColor: segment.color,
          color: segment.color,
          borderRadius: '12px',
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px',
          '&:hover': {
            borderColor: segment.color,
            background: `${segment.color}15`,
            boxShadow: `0 4px 16px ${segment.color}30`,
          },
        }}
      >
        View & Reach Out
      </Button>
    </Card>
  )
}

// Custom Segment Dialog Component
interface CustomSegmentDialogProps {
    open: boolean
    onClose: () => void
    editingSegment: Omit<Segment, 'followers' | 'avgEngagement'> | null
    onSave: (segment: Omit<Segment, 'followers' | 'avgEngagement'>) => void
  }

const CustomSegmentDialog: React.FC<CustomSegmentDialogProps> = ({ open, onClose, editingSegment, onSave }) => {
  const [name, setName] = useState(editingSegment?.name ?? '')
  const [description, setDescription] = useState(editingSegment?.description ?? '')
  const [color, setColor] = useState(editingSegment?.color ?? '#a855f7')
  const [icon, setIcon] = useState(editingSegment?.icon ?? 'Users')
  const [minFollowers, setMinFollowers] = useState(editingSegment?.criteria.minFollowers?.toString() ?? '')
  const [maxFollowers, setMaxFollowers] = useState(editingSegment?.criteria.maxFollowers?.toString() ?? '')
  const [minLikes, setMinLikes] = useState(editingSegment?.criteria.minLikes?.toString() ?? '')
  const [minComments, setMinComments] = useState(editingSegment?.criteria.minComments?.toString() ?? '')
  const [hashtags, setHashtags] = useState(editingSegment?.criteria.hashtags?.join(', ') ?? '')
  const [industries, setIndustries] = useState(editingSegment?.criteria.industries?.join(', ') ?? '')

  const handleSave = (): void => {
    const segment: Omit<Segment, 'followers' | 'avgEngagement'> = {
      id: editingSegment?.id ?? `custom-${Date.now()}`,
      name,
      description,
      color,
      icon,
      criteria: {
        minFollowers: minFollowers ? Number(minFollowers) : undefined,
        maxFollowers: maxFollowers ? Number(maxFollowers) : undefined,
        minLikes: minLikes ? Number(minLikes) : undefined,
        minComments: minComments ? Number(minComments) : undefined,
        hashtags: hashtags ? hashtags.split(',').map(h => h.trim()).filter(h => h) : undefined,
        industries: industries ? industries.split(',').map(i => i.trim()).filter(i => i) : undefined,
      },
    }
    onSave(segment)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)' }}>
        {editingSegment ? 'Edit Custom Segment' : 'Create Custom Segment'}
      </DialogTitle>
      <DialogContent sx={{ background: '#0f172a', pt: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Segment Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Icon</InputLabel>
              <Select value={icon} onChange={(e) => setIcon(e.target.value)} label="Icon">
                {Object.keys(iconMap).map(iconName => (
                  <MenuItem key={iconName} value={iconName}>{iconName}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Color"
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              fullWidth
            />
          </Box>
          <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 600, color: '#a855f7' }}>
            Segment Criteria
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Min Followers"
              type="number"
              value={minFollowers}
              onChange={(e) => setMinFollowers(e.target.value)}
              fullWidth
            />
            <TextField
              label="Max Followers"
              type="number"
              value={maxFollowers}
              onChange={(e) => setMaxFollowers(e.target.value)}
              fullWidth
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Min Likes"
              type="number"
              value={minLikes}
              onChange={(e) => setMinLikes(e.target.value)}
              fullWidth
            />
            <TextField
              label="Min Comments"
              type="number"
              value={minComments}
              onChange={(e) => setMinComments(e.target.value)}
              fullWidth
            />
          </Box>
          <TextField
            label="Hashtags (comma-separated)"
            value={hashtags}
            onChange={(e) => setHashtags(e.target.value)}
            fullWidth
            placeholder="fitness, wellness, health"
          />
          <TextField
            label="Industries/Keywords (comma-separated)"
            value={industries}
            onChange={(e) => setIndustries(e.target.value)}
            fullWidth
            placeholder="technology, design, marketing"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ background: '#0f172a', p: 3 }}>
        <Button onClick={onClose} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          sx={{
            background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #9333ea 0%, #db2777 100%)',
            },
          }}
        >
          {editingSegment ? 'Save Changes' : 'Create Segment'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// Segment Details Dialog Component
interface SegmentDetailsDialogProps {
  segment: Segment | null
  open: boolean
  onClose: () => void
}

const SegmentDetailsDialog: React.FC<SegmentDetailsDialogProps> = ({ segment, open, onClose }) => {
  if (!segment) return null

  const Icon = iconMap[segment.icon as keyof typeof iconMap] ?? Users

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '16px',
              background: `linear-gradient(135deg, ${segment.color}30, ${segment.color}60)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon size={28} style={{ color: segment.color }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: '#fff' }}>{segment.name}</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              {segment.followers.length} followers • {segment.avgEngagement.toFixed(1)}% avg engagement
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ background: '#0f172a', pt: 3 }}>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#a855f7' }}>
            Top Followers in This Segment
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {segment.followers.slice(0, 10).map(follower => (
              <Box
                key={follower.username}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  padding: 2,
                  borderRadius: '16px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.08)',
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <Box
                  component="img"
                  src={follower.profileImage ?? `https://ui-avatars.com/api/?name=${follower.username}&background=a855f7&color=fff`}
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    border: `2px solid ${segment.color}`,
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#fff' }}>
                    @{follower.username}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                    {follower.stats.followers} followers • {follower.stats.posts} posts
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <IconButton
                    size="small"
                    onClick={() => window.open(`https://instagram.com/direct/t/${follower.username}`, '_blank')}
                    sx={{
                      color: segment.color,
                      border: `1px solid ${segment.color}40`,
                      '&:hover': {
                        background: `${segment.color}20`,
                        borderColor: segment.color,
                      },
                    }}
                    title="Send DM"
                  >
                    <Send size={16} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => window.open(`https://instagram.com/${follower.username}`, '_blank')}
                    sx={{
                      color: segment.color,
                      border: `1px solid ${segment.color}40`,
                      '&:hover': {
                        background: `${segment.color}20`,
                        borderColor: segment.color,
                      },
                    }}
                    title="View Profile"
                  >
                    <ExternalLink size={16} />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ background: '#0f172a', p: 3, justifyContent: 'space-between' }}>
        <Button onClick={onClose} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          Close
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Send size={18} />}
            onClick={() => {
              const usernames = segment.followers.slice(0, 10).map(f => f.username).join(', ')
              navigator.clipboard.writeText(usernames).catch(console.error)
            }}
            sx={{
              borderColor: segment.color,
              color: segment.color,
              '&:hover': {
                borderColor: segment.color,
                background: `${segment.color}15`,
              },
            }}
          >
            Copy Usernames
          </Button>
          <Button
            variant="contained"
            startIcon={<ExternalLink size={18} />}
            onClick={() => {
              segment.followers.slice(0, 5).forEach((f, i) => {
                setTimeout(() => {
                  window.open(`https://instagram.com/${f.username}`, '_blank')
                }, i * 500)
              })
            }}
            sx={{
              background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #9333ea 0%, #db2777 100%)',
              },
            }}
          >
            Open Top 5 Profiles
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export default NalapodFollowerSegmentation